import React, { useState, useEffect } from 'react'
import { Navigate, useLocation, Link, useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Loader2, ArrowRight, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import apiService from '../services/api'
import { LoginForm } from '../types'
import PlatformBusinessRegistration from '../components/PlatformBusinessRegistration'

const Login: React.FC = () => {
  const location = useLocation()
  const prefillEmail = (location.state as { email?: string } | null)?.email
  const [formData, setFormData] = useState<LoginForm>({
    email: prefillEmail || '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const error = searchParams.get('error')
    const message = searchParams.get('message')
    if (error && message) {
      toast.error(decodeURIComponent(message))
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  if (user) {
    const state = location.state as { from?: { pathname?: string }; afterClaimProfile?: boolean } | null
    if (state?.afterClaimProfile) {
      return <Navigate to="/profile/school-documents" replace />
    }
    const from = state?.from?.pathname || '/dashboard'
    return <Navigate to={from} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await login(formData.email, formData.password)
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.message || ''
      if (typeof msg === 'string' && msg.toLowerCase().includes('verify your email')) {
        try {
          await apiService.resendVerificationEmail(formData.email.trim())
          toast('Please verify your email first. We\'ve sent a verification code to your inbox.', { icon: '✉️' })
        } catch {
          toast.error('Could not resend code. You can request a new one on the verification page.')
        }
        navigate('/verify-email', { state: { email: formData.email }, replace: true })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-200 bg-white rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'

  return (
    <div className="min-h-screen flex bg-primary-50">
      {/* Left Panel — Brand (light + orange theme) */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-primary-200/70 via-primary-100 to-white text-gray-900 flex-col justify-between p-12 overflow-hidden border-r border-primary-200/50">
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-primary-300/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl" />

        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <img src="/logo.png" alt="BookMyYogaTTC" className="h-12 w-auto mb-16" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl font-bold leading-tight tracking-tight text-gray-900"
          >
            Welcome back to
            <br />
            <span className="text-primary-600">your dashboard.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-gray-600 text-base leading-relaxed max-w-md"
          >
            Manage your courses, track bookings, and grow your yoga school — all from one place.
          </motion.p>

        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative z-10 bg-primary-200/40 border border-primary-300/50 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <p className="text-sm font-semibold text-gray-900">What's new</p>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            Advanced analytics, streamlined course management, and a brand-new verification flow to help you build trust faster.
          </p>
        </motion.div>

        <div className="relative z-10 mt-8 space-y-4">
          <PlatformBusinessRegistration />
          <div className="text-xs text-gray-500">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Built for yoga schools worldwide
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mt-1"
            >
              Contact us:{' '}
              <a href="mailto:info@bookmyyogattc.com" className="text-primary-600 hover:underline">
                info@bookmyyogattc.com
              </a>
            </motion.p>
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-12 bg-white">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.png" alt="BookMyYogaTTC" className="h-10 w-auto" />
          </div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Sign in to your account</h2>
            <p className="mt-1.5 text-sm text-gray-500">
              Enter your credentials to access your dashboard.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 space-y-5"
            onSubmit={handleSubmit}
          >
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={inputClass}
                placeholder="you@yourschool.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={inputClass + ' pr-10'}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-xs text-gray-600">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-500 pt-2">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                Register your school
              </Link>
            </p>
            <p className="text-center text-sm text-gray-500">
              Profile created by our team?{' '}
              <Link to="/claim-profile" className="font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                Claim profile
              </Link>
            </p>

            <div className="lg:hidden pt-5 mt-2 border-t border-gray-100">
              <PlatformBusinessRegistration />
            </div>
          </motion.form>
        </div>
      </div>
    </div>
  )
}

export default Login
